// Reference lists for marketing-channel attribution.
// This file is the source of truth for these lists; docs/attribution-channels.md (§1–§2)
// explains how they're used. Changing these lists changes classification.

// Paid-search click IDs (§2 step 1). Presence of any of these → Paid Search.
export const PAID_SEARCH_CLICK_IDS = [
  'gclid', // Google Ads (web)
  'gbraid', // Google Ads (iOS app conversions, post-ATT)
  'wbraid', // Google Ads (web→app, post-ATT)
  'msclkid', // Microsoft / Bing Ads
  'yclid', // Yandex / Yahoo Japan
] as const;

// Paid-social click IDs that are reliably paid (§2 step 1).
// fbclid / ttclid are deliberately NOT here — they appear on organic clicks too.
export const PAID_SOCIAL_CLICK_IDS = ['li_fat_id'] as const;

// Organic-disambiguator click IDs — added to both organic and paid clicks.
// Only a paid utm_medium can promote these to Paid Social (§2 step 2 + the fbclid note after it).
export const AMBIGUOUS_SOCIAL_CLICK_IDS = ['fbclid', 'ttclid'] as const;

// utm_medium value sets, lower-cased (§2 step 2).
export const UTM_MEDIUM: Record<string, string[]> = {
  paidSearch: ['cpc', 'ppc', 'paidsearch', 'paid-search', 'sem'],
  paidSocial: ['paidsocial', 'paid-social', 'social-cpc', 'social-ppc'],
  email: ['email'],
  organicSocial: ['social', 'social-organic', 'organic-social', 'sm'],
  organicSearch: ['organic'],
  referral: ['referral', 'partner'],
};

// utm_source values that imply Email when no medium is present (§2 step 3).
export const EMAIL_UTM_SOURCES = [
  'newsletter',
  'transactional',
  'mailchimp',
  'sendgrid',
  'hubspot-email',
  'postmark',
  'klaviyo',
];

// Search-engine referrer hosts (§2 step 4). Matched by registrable-domain suffix.
export const SEARCH_ENGINE_HOSTS = [
  'google.com', // also covers google.co.uk, google.ca, google.<any TLD> via suffix match
  'bing.com',
  'duckduckgo.com',
  'yahoo.com',
  'search.yahoo.com',
  'ecosia.org',
  'brave.com',
  'search.brave.com',
  'kagi.com',
  'you.com',
  'baidu.com',
  'yandex.com',
  'yandex.ru',
];

// Google/Yandex country TLDs can't be enumerated; treat any host containing these brand
// labels (e.g. www.google.co.uk) as a search engine.
export const SEARCH_ENGINE_HOST_LABELS = ['google', 'yandex'];

// Social-platform referrer hosts (§2 step 4).
export const SOCIAL_HOSTS = [
  'facebook.com',
  'm.facebook.com',
  'l.facebook.com',
  'lm.facebook.com',
  'instagram.com',
  'l.instagram.com',
  'tiktok.com',
  'x.com',
  'twitter.com',
  't.co',
  'linkedin.com',
  'lnkd.in',
  'youtube.com',
  'm.youtube.com',
  'pinterest.com',
  'pin.it',
  'reddit.com',
  'old.reddit.com',
  'snapchat.com',
  'threads.net',
];

// utm_source values that imply a social platform (§2 step 3 disambiguation). Lower-cased.
export const SOCIAL_UTM_SOURCES = [
  'facebook',
  'fb',
  'instagram',
  'ig',
  'tiktok',
  'twitter',
  'x',
  'linkedin',
  'youtube',
  'pinterest',
  'reddit',
  'snapchat',
  'threads',
];

// Generic paid mediums whose channel depends on context (social vs search).
export const GENERIC_PAID_MEDIUMS = ['cpc', 'ppc'];

// Webmail referrer hosts (§2 step 4 Email).
export const WEBMAIL_HOSTS = [
  'mail.google.com',
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'mail.yahoo.com',
  'mail.proton.me',
];

export const TRAFFIC_SOURCE = {
  DIRECT: 'direct',
  ORGANIC_SEARCH: 'organic_search',
  PAID_SEARCH: 'paid_search',
  ORGANIC_SOCIAL: 'organic_social',
  PAID_SOCIAL: 'paid_social',
  EMAIL: 'email',
  REFERRAL: 'referral',
};
